import { ApiError, type ApiViolation } from "@/api/client";
import { hasMessage } from "./catalog";
import type { Params, Translate } from "./translate";

/**
 * The sentence to show for one rejected field.
 *
 * The API sends a code, the numbers the sentence needs, and an English message.
 * A code the catalogue knows is rendered in the reader's language; one it does
 * not is shown as the API wrote it (K-36).
 *
 * That fallback is deliberate and it is not a gap to be closed later. Some
 * rejections only a broken client can provoke — a malformed identifier, a body
 * that is not an object — and translating those would be work nobody reads.
 * What matters is that the untranslated case shows a real sentence rather than a
 * code, a blank, or the word "undefined" under an input somebody is still
 * looking at.
 */
export function violationMessage(t: Translate, violation: ApiViolation): string {
  const key = `violation.${violation.code}`;
  return hasMessage(key) ? t(key, violation.params) : violation.message;
}

/**
 * The sentence to show for a whole failed request.
 *
 * Plan limits are the interesting case: the code names *which* limit was hit
 * (K-35), so the screen writes the sentence itself and does not have to parse
 * one. Everything else falls back to the API's own message, for the same reason
 * as above.
 */
export function apiMessage(t: Translate, error: unknown, params?: Params): string {
  if (!(error instanceof ApiError)) {
    return error instanceof Error ? error.message : String(error);
  }

  switch (error.code) {
    case "plan_limit_accounts":
      return t("plan.limit.accounts");
    case "plan_limit_import":
      return t("plan.limit.import");
    case "plan_limit":
      return t("plan.limit.generic");
    case "plan_limit_transactions":
      // This sentence needs the month's allowance, which the *screen* knows
      // because it asked /allowance for its countdown, and the error does not
      // carry. Without it the English message is the better answer: it is a real
      // sentence with the real number in it, rather than a German one with a
      // hole where the number should be.
      return params?.allowed === undefined
        ? error.message
        : t("plan.limit.transactions", params);
  }

  const key = `error.${error.code}`;
  return hasMessage(key) ? t(key) : error.message;
}

/**
 * The per-field messages for a failed mutation, in the reader's language.
 *
 * Every form needs the same three lines — is this an ApiError, does it name this
 * field, what does that code say in German — so they live here once. A form
 * calls `violation("name")` and gets a sentence or nothing.
 */
export function violationsOf(t: Translate, error: unknown) {
  return (field: string): string | undefined => {
    if (!(error instanceof ApiError)) return undefined;
    const found = error.violationFor(field);
    return found ? violationMessage(t, found) : undefined;
  };
}
